import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import rootStore from "../../../stores/rootStore.js";
import {useParams} from "react-router-dom";
import {scheduledOfflineApi} from "../../../api/scheduledOffline";
import {taxAuthApi} from "../../../api/taxAuth";
import { Badge, Button, Card, Col, Container, Form, Row, Table, Tabs, Tab, Alert } from "react-bootstrap";
import {authApi} from "../../../api/auth.js";
import HeroSection from "../../../components/HeroSection.jsx";

function TaxAuthDetails() {
    const {authenticationStore: authStore} = rootStore;
    const {taxAuthId} = useParams();
    const [taxAuth, setTaxAuth] = useState(null);
    const [outages, setOutages] = useState([]);
    const [formData, setFormData] = useState(null);
    const [formStatus, setFormStatus] = useState({saving: false, success: false, error: ""});
    const [users, setUsers] = useState([]);
    const [invite, setInvite] = useState({firstName: "", lastName: "", email: "", phoneNumber: "", role: "MuniAdmin"});
    const [inviteStatus, setInviteStatus] = useState({saving: false, success: false, error: ""});
    const [newOutage, setNewOutage] = useState({
        offlineReason: '',
        offlineStart: '',
        offlineEnd: ''
    });

    useEffect(() => {
        const fetchTaxAuth = async () => {
            const response = await taxAuthApi.getTaxAuthInfo(taxAuthId);
            setTaxAuth(response);
            setFormData({
                name: response.name || "",
                taxAuthorityCode: response.taxAuthorityCode || "",
                online: response.online || false,
                offlineReason: response.offlineReason || "",
                address: response.address || "",
                city: response.city || "",
                prov: response.prov || "",
                postCode: response.postCode || "",
                country: response.country || "",
                stripeConnectedAccountId: response.stripeConnectedAccountId || "",
                supportsPidSearch: response.supportsPidSearch || false,
                supportsMhrSearch: response.supportsMhrSearch || false,
                supportsRollSearch: response.supportsRollSearch || false,
                supportsAddressSearch: response.supportsAddressSearch || false,
                technicalContactName: response.technicalContactName || "",
                technicalContactPhone: response.technicalContactPhone || "",
                technicalContactEmail: response.technicalContactEmail || "",
                financialContactName: response.financialContactName || "",
                financialContactPhone: response.financialContactPhone || "",
                financialContactEmail: response.financialContactEmail || ""
            });
        };
        const fetchOutages = async () => {
            const response = await scheduledOfflineApi.getScheduledOffline(taxAuthId, null, true);
            setOutages(response);
        };
        const fetchUsers = async () => {
            try {
                const response = await taxAuthApi.getTaxAuthUsers(taxAuthId);
                setUsers(response);
            } catch (e) {
                console.error("Failed to load users", e);
            }
        };
        fetchTaxAuth();
        fetchOutages();
        fetchUsers();
    }, [taxAuthId]);

    const handleInputChange = (event) => {
        const {name, value} = event.target;
        setNewOutage({...newOutage, [name]: value});
    };

    const handleAddOutage = async () => {
        await scheduledOfflineApi.addScheduledOffline(newOutage.offlineReason, newOutage.offlineStart, newOutage.offlineEnd, taxAuthId);
        const response = await scheduledOfflineApi.getScheduledOffline(taxAuthId, null, true);
        setOutages(response);
        setNewOutage({
            offlineReason: '',
            offlineStart: '',
            offlineEnd: ''
        });
    };

    const handleDeleteOutage = async (outageId) => {
        await scheduledOfflineApi.deleteScheduledOffline(outageId);
        const response = await scheduledOfflineApi.getScheduledOffline(taxAuthId, null, true);
        setOutages(response);
    };

    if (!taxAuth || !formData) {
        return <Container className="dashboard-shell"><p>Loading...</p></Container>;
    }

    const handleMasquerade = async () => {
        const response = await authApi.masquerade(taxAuth.userId);
        authStore.setAuthInfo(response);
        window.location.reload();
    }

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Tax Authority"
                title={`${taxAuth.name} Details`}
                subtitle="Manage outages and access for this municipality."
                actions={(authStore.isSuperUser || authStore.isSystemAdmin) && (
                    <Button variant="secondary" onClick={handleMasquerade}>
                        Login As
                    </Button>
                )}
            />

            <Tabs defaultActiveKey="details" className="mt-4">
                <Tab eventKey="details" title="Details">
                    <Card className="dashboard-panel mt-3">
                        <Card.Body>
                            {formStatus.error && <Alert variant="danger">{formStatus.error}</Alert>}
                            {formStatus.success && <Alert variant="success">Tax authority updated.</Alert>}
                            <Form onSubmit={async (e) => {
                                e.preventDefault();
                                setFormStatus({saving: true, success: false, error: ""});
                                try {
                                    await taxAuthApi.updateTaxAuth(taxAuthId, {
                                        name: formData.name,
                                        address: formData.address,
                                        city: formData.city,
                                        prov: formData.prov,
                                        country: formData.country,
                                        postCode: formData.postCode,
                                        online: formData.online,
                                        offlineReason: formData.offlineReason,
                                        stripeConnectedAccountId: formData.stripeConnectedAccountId,
                                        supportsPidSearch: formData.supportsPidSearch,
                                        supportsMhrSearch: formData.supportsMhrSearch,
                                        supportsRollSearch: formData.supportsRollSearch,
                                        supportsAddressSearch: formData.supportsAddressSearch,
                                        technicalContactName: formData.technicalContactName,
                                        technicalContactPhone: formData.technicalContactPhone,
                                        technicalContactEmail: formData.technicalContactEmail,
                                        financialContactName: formData.financialContactName,
                                        financialContactPhone: formData.financialContactPhone,
                                        financialContactEmail: formData.financialContactEmail
                                    });
                                    setFormStatus({saving: false, success: true, error: ""});
                                } catch (err) {
                                    console.error(err);
                                    setFormStatus({saving: false, success: false, error: "Failed to update tax authority"});
                                }
                            }}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}/>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tax Authority Code</Form.Label>
                                            <Form.Control value={formData.taxAuthorityCode} readOnly />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Stripe Connected Account Id</Form.Label>
                                            <Form.Control value={formData.stripeConnectedAccountId} onChange={(e) => setFormData({...formData, stripeConnectedAccountId: e.target.value})}/>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Offline Reason</Form.Label>
                                            <Form.Control value={formData.offlineReason} onChange={(e) => setFormData({...formData, offlineReason: e.target.value})}/>
                                        </Form.Group>
                                        <Form.Check
                                            className="mb-2"
                                            type="switch"
                                            label="Online"
                                            checked={formData.online}
                                            onChange={(e) => setFormData({...formData, online: e.target.checked})}
                                        />
                                        <Form.Check
                                            className="mb-2"
                                            type="switch"
                                            label="Supports PID Search"
                                            checked={formData.supportsPidSearch}
                                            onChange={(e) => setFormData({...formData, supportsPidSearch: e.target.checked})}
                                        />
                                        <Form.Check
                                            className="mb-2"
                                            type="switch"
                                            label="Supports MHR Search"
                                            checked={formData.supportsMhrSearch}
                                            onChange={(e) => setFormData({...formData, supportsMhrSearch: e.target.checked})}
                                        />
                                        <Form.Check
                                            className="mb-2"
                                            type="switch"
                                            label="Supports Roll Search"
                                            checked={formData.supportsRollSearch}
                                            onChange={(e) => setFormData({...formData, supportsRollSearch: e.target.checked})}
                                        />
                                        <Form.Check
                                            className="mb-2"
                                            type="switch"
                                            label="Supports Address Search"
                                            checked={formData.supportsAddressSearch}
                                            onChange={(e) => setFormData({...formData, supportsAddressSearch: e.target.checked})}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Address</Form.Label>
                                            <Form.Control value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})}/>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>City</Form.Label>
                                            <Form.Control value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})}/>
                                        </Form.Group>
                                        <Row className="mb-3">
                                            <Col md={4}>
                                                <Form.Label>Prov</Form.Label>
                                                <Form.Control value={formData.prov} onChange={(e) => setFormData({...formData, prov: e.target.value})}/>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Label>Post Code</Form.Label>
                                                <Form.Control value={formData.postCode} onChange={(e) => setFormData({...formData, postCode: e.target.value})}/>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Label>Country</Form.Label>
                                                <Form.Control value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})}/>
                                            </Col>
                                        </Row>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Technical Contact</Form.Label>
                                            <Form.Control className="mb-2" placeholder="Name" value={formData.technicalContactName} onChange={(e) => setFormData({...formData, technicalContactName: e.target.value})}/>
                                            <Form.Control className="mb-2" placeholder="Phone" value={formData.technicalContactPhone} onChange={(e) => setFormData({...formData, technicalContactPhone: e.target.value})}/>
                                            <Form.Control placeholder="Email" value={formData.technicalContactEmail} onChange={(e) => setFormData({...formData, technicalContactEmail: e.target.value})}/>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Financial Contact</Form.Label>
                                            <Form.Control className="mb-2" placeholder="Name" value={formData.financialContactName} onChange={(e) => setFormData({...formData, financialContactName: e.target.value})}/>
                                            <Form.Control className="mb-2" placeholder="Phone" value={formData.financialContactPhone} onChange={(e) => setFormData({...formData, financialContactPhone: e.target.value})}/>
                                            <Form.Control placeholder="Email" value={formData.financialContactEmail} onChange={(e) => setFormData({...formData, financialContactEmail: e.target.value})}/>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end">
                                    <Button type="submit" disabled={formStatus.saving}>{formStatus.saving ? "Saving..." : "Save changes"}</Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="users" title="Users">
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white"><strong>Invite User</strong></Card.Header>
                        <Card.Body>
                            {inviteStatus.error && <Alert variant="danger">{inviteStatus.error}</Alert>}
                            {inviteStatus.success && <Alert variant="success">Invite sent.</Alert>}
                            <Form onSubmit={async (e) => {
                                e.preventDefault();
                                setInviteStatus({saving: true, success: false, error: ""});
                                try {
                                    await taxAuthApi.inviteTaxAuthUser(taxAuthId, {
                                        firstName: invite.firstName,
                                        lastName: invite.lastName,
                                        email: invite.email,
                                        phoneNumber: invite.phoneNumber,
                                        role: invite.role
                                    });
                                    setInviteStatus({saving: false, success: true, error: ""});
                                    const refreshed = await taxAuthApi.getTaxAuthUsers(taxAuthId);
                                    setUsers(refreshed);
                                } catch (err) {
                                    console.error(err);
                                    setInviteStatus({saving: false, success: false, error: "Failed to send invite"});
                                }
                            }}>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label>First Name</Form.Label>
                                        <Form.Control value={invite.firstName} onChange={(e) => setInvite({...invite, firstName: e.target.value})}/>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Last Name</Form.Label>
                                        <Form.Control value={invite.lastName} onChange={(e) => setInvite({...invite, lastName: e.target.value})}/>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Email</Form.Label>
                                        <Form.Control value={invite.email} onChange={(e) => setInvite({...invite, email: e.target.value})}/>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control value={invite.phoneNumber} onChange={(e) => setInvite({...invite, phoneNumber: e.target.value})}/>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Role</Form.Label>
                                        <Form.Select value={invite.role} onChange={(e) => setInvite({...invite, role: e.target.value})}>
                                            <option value="MuniAdmin">Muni Admin</option>
                                            <option value="MuniFinanceUser">Muni Finance</option>
                                            <option value="MuniTechUser">Muni Tech</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={4} className="d-flex align-items-end">
                                        <Button type="submit" disabled={inviteStatus.saving}>
                                            {inviteStatus.saving ? "Sending..." : "Send Invite"}
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        </Card.Body>
                    </Card>
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Users</strong>
                            <Badge bg="light" text="dark">{users.length}</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table striped hover responsive className="mb-0">
                                <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                </tr>
                                </thead>
                                <tbody>
                                {users.map((u) => (
                                    <tr key={u.id}>
                                        <td>{u.firstName} {u.lastName}</td>
                                        <td>{u.email}</td>
                                        <td>{u.phoneNumber}</td>
                                        <td>{u.role}</td>
                                        <td>{u.active ? "Active" : "Pending"}</td>
                                    </tr>
                                ))}
                                {!users.length && (
                                    <tr>
                                        <td colSpan={5} className="text-center text-muted py-3">No users found.</td>
                                    </tr>
                                )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
                <Tab eventKey="outages" title="Outages">
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white"><strong>Outage Management</strong></Card.Header>
                        <Card.Body>
                            <Form>
                                <Row className="g-3">
                                    <Form.Group as={Col} md={4} className="mb-3">
                                        <Form.Label>Reason</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="offlineReason"
                                            value={newOutage.offlineReason}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={4} className="mb-3">
                                        <Form.Label>Start Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            name="offlineStart"
                                            value={newOutage.offlineStart}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={4} className="mb-3">
                                        <Form.Label>End Time</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            name="offlineEnd"
                                            value={newOutage.offlineEnd}
                                            onChange={handleInputChange}
                                        />
                                    </Form.Group>
                                </Row>
                                <Button variant="primary" onClick={handleAddOutage}>
                                    Add Outage
                                </Button>
                            </Form>
                        </Card.Body>
                    </Card>

                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <strong>Current and Upcoming Outages</strong>
                            <Badge bg="light" text="dark">{outages.length}</Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table striped bordered hover responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>Reason</th>
                                        <th>Start Time</th>
                                        <th>End Time</th>
                                        <th style={{width: 120}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {outages.map((outage) => (
                                        <tr key={outage.id}>
                                            <td>{outage.reason}</td>
                                            <td>{new Date(outage.offlineStart).toLocaleString()}</td>
                                            <td>{new Date(outage.offlineEnd).toLocaleString()}</td>
                                            <td>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteOutage(outage.id)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {!outages.length && (
                                        <tr>
                                            <td colSpan={4} className="text-center text-muted py-3">No outages scheduled.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Tab>
            </Tabs>
        </Container>
    );
}

export default observer(TaxAuthDetails);
